import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Container,
    Grid,
    Button,
    CircularProgress,
    Alert,
    TextField,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';

interface UserInfo {
    id: number;
    email: string;
    name: string;
    grade: string;
    created_at: string;
}

const MyPage: React.FC = () => {
    const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { enqueueSnackbar } = useSnackbar();
    const { logout } = useAuth();

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [changePasswordLoading, setChangePasswordLoading] = useState(false);

    useEffect(() => {
        fetchUserInfo();
    }, []);

    const fetchUserInfo = async () => {
        try {
            const response = await api.get('/api/users/me');
            setUserInfo(response.data);
        } catch (error) {
            console.error('사용자 정보 조회 실패:', error);
            setError('사용자 정보를 불러오는데 실패했습니다.');
            enqueueSnackbar('사용자 정보를 불러오는데 실패했습니다.', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        logout();
    };

    const handleChangePassword = async () => {
        setChangePasswordLoading(true);
        try {
            await api.post('/api/users/change-password', {
                current_password: currentPassword,
                new_password: newPassword,
            });
            enqueueSnackbar('비밀번호가 성공적으로 변경되었습니다.', { variant: 'success' });
            setCurrentPassword('');
            setNewPassword('');
        } catch (error: any) {
            enqueueSnackbar(
                error.response?.data?.detail || '비밀번호 변경에 실패했습니다.',
                { variant: 'error' }
            );
        } finally {
            setChangePasswordLoading(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Container maxWidth="md" sx={{ mt: 4 }}>
                <Alert severity="error">{error}</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ mt: 4 }}>
            <Paper sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom>
                    마이페이지
                </Typography>

                {userInfo && (
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" color="textSecondary">이메일</Typography>
                            <Typography variant="body1">{userInfo.email}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" color="textSecondary">이름</Typography>
                            <Typography variant="body1">{userInfo.name}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" color="textSecondary">등급</Typography>
                            <Typography variant="body1">{userInfo.grade}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" color="textSecondary">가입일</Typography>
                            <Typography variant="body1">{new Date(userInfo.created_at).toLocaleDateString()}</Typography>
                        </Grid>

                        {/* 🔐 비밀번호 변경 폼 */}
                        <Grid item xs={12}>
                            <TextField
                                label="현재 비밀번호"
                                type="password"
                                fullWidth
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="새 비밀번호"
                                type="password"
                                fullWidth
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={handleChangePassword}
                                disabled={changePasswordLoading}
                            >
                                {changePasswordLoading ? '변경 중...' : '비밀번호 변경'}
                            </Button>
                        </Grid>

                        <Grid item xs={12}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleLogout}
                                sx={{ mt: 2 }}
                            >
                                로그아웃
                            </Button>
                        </Grid>
                    </Grid>
                )}
            </Paper>
        </Container>
    );
};

export default MyPage;